import React, { useState, useMemo, useCallback } from 'react';
import { stagePublicAPI } from '../services/api';
import { dateFr } from '../services/dates';
import './StagePublic.css';

/**
 * SUIVI DE STAGE — la GRILLE D'ÉVALUATION du maître de stage (lot 3.6)
 * ====================================================================
 * Spec : doc/modules/suivi_de_stage.md, étape ⑨, écarts E100 → E102
 * Maquette : l'aperçu « Grille d'évaluation (vue maître de stage) » —
 * `#psc-apercu-eval` : en-tête vert, encadré stagiaire / entreprise / période,
 * un bloc `.eval-critere` par critère avec ses niveaux en pastilles, le total,
 * le commentaire général, « Valider l'évaluation ».
 *
 * ── CE QUE LA MAQUETTE NE DESSINE PAS, ET QUI EST AJOUTÉ (E102) ────────────
 *   · le SIGNATAIRE, obligatoire : une évaluation doit engager quelqu'un ;
 *   · un RÉCAPITULATIF avant l'envoi : la réponse est définitive, le maître
 *     relit ce qu'il signe ;
 *   · un bouton grisé QUI DIT CE QUI MANQUE (E69), jamais un gris muet.
 *
 * ⚠️ LES NIVEAUX SONT DE VRAIS BOUTONS RADIO, habillés en pastilles : le clavier
 * (Tab, flèches) et le doigt marchent sans une ligne de code de plus.
 *
 * ⚠️ La grille affichée est la COPIE FIGÉE de la campagne (E101), jamais le
 * modèle vivant : c'est le serveur qui l'envoie, et qui recontrôle la réponse.
 */

const pts = (n) => `${n} pt${n > 1 ? 's' : ''}`;

function Message({ ton, icone, titre, children }) {
    return (
        <div className="pub-page">
            <div className={'pub-message' + (ton ? ' pub-message-' + ton : '')}>
                <i className={'fa-solid ' + icone} />
                <h2>{titre}</h2>
                {children}
            </div>
        </div>
    );
}

/**
 * ⚠️ LOT 3.8a — L'APERÇU DE L'ÉDITEUR DE MODÈLES, C'EST CETTE PAGE (E116).
 * `apercu` neutralise l'envoi (rien ne part au serveur). Un aperçu dessiné à part
 * finirait par montrer autre chose que ce que reçoit le destinataire.
 * ⚠️ Lot 3.5c : la fiche de visite a quitté cette page — elle a la sienne
 * (`StageVisitePublique` : date, mode, durée, « Remplie par », modification).
 */
const VARIANTES = {
    eval:   { titre: 'Évaluation de stage', icone: 'fa-star-half-stroke', personne: 'Stagiaire', signataire: true }
};

function StageEvaluationPublique({ token, page, apercu = false, variante = 'eval' }) {
    const v = VARIANTES[variante] || VARIANTES.eval;
    const d = page.dossier || {};
    const grille = page.grille || { criteres: [] };
    const masquer = grille.masquer_points === true;

    const [niveaux, setNiveaux] = useState({});          // critere_id → niveau_id
    const [commentaires, setCommentaires] = useState({}); // critere_id → texte
    const [commentaire, setCommentaire] = useState('');
    const [signataire, setSignataire] = useState('');
    const [recap, setRecap] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [fin, setFin] = useState(null);                 // { ton, titre, texte }

    const choisi = useCallback((c) => c.niveaux.find((n) => String(n.id) === String(niveaux[c.id])), [niveaux]);
    const total = useMemo(() => grille.criteres.reduce((s, c) => s + (choisi(c)?.points || 0), 0), [grille, choisi]);

    /** Ce qui manque, en toutes lettres — c'est ce que dit le bouton grisé. */
    const manque = useMemo(() => {
        // Les NUMÉROS affichés à l'écran (1, 2, 3…), pas les identifiants.
        const sansNiveau = grille.criteres.map((c, i) => (choisi(c) ? null : i + 1)).filter(Boolean);
        const liste = [];
        if (sansNiveau.length === grille.criteres.length) liste.push('un niveau pour chaque critère');
        else if (sansNiveau.length) {
            liste.push(`un niveau pour ${sansNiveau.length > 1 ? 'les critères' : 'le critère'} ${sansNiveau.join(', ')}`);
        }
        if (v.signataire && !signataire.trim()) liste.push('le nom du signataire');
        return liste;
    }, [grille, choisi, signataire, v.signataire]);

    const envoyer = useCallback(async () => {
        if (apercu) {
            setFin({ ton: 'succes', icone: 'fa-eye', titre: 'Aperçu : rien n’a été envoyé',
                     texte: 'Dans la vraie page, c’est ici que le destinataire verrait la confirmation de son envoi.' });
            return;
        }
        setEnCours(true); setErreur(null);
        try {
            const r = await stagePublicAPI.soumettreEvaluation(token, {
                niveaux, commentaires, commentaire, signataire
            });
            setFin({ ton: 'succes', icone: 'fa-circle-check', titre: 'Évaluation envoyée', texte: r.data.message });
        } catch (e) {
            if (e.response?.status === 410) {
                setFin({ ton: 'erreur', icone: 'fa-link-slash', titre: e.response.data?.error || 'Lien indisponible',
                         texte: e.response.data?.detail || '' });
            } else {
                setErreur(e.response?.data?.error || "L'envoi n'a pas abouti. Réessayez dans un instant.");
                setRecap(false);
            }
        } finally { setEnCours(false); }
    }, [apercu, token, niveaux, commentaires, commentaire, signataire]);

    if (fin) {
        return (
            <Message ton={fin.ton} icone={fin.icone} titre={fin.titre}>
                <p>{fin.texte}</p>
                <p className="pub-discret">Vous pouvez fermer cette page.</p>
            </Message>
        );
    }

    const enTete = (
        <div className="form-public-header">
            <h2><i className={'fa-solid ' + v.icone} /> {v.titre}</h2>
            <p>{d.departement_nom}{d.formation_nom ? ` · ${d.formation_nom}` : ''}{d.semestre ? ` · ${d.semestre}` : ''}</p>
        </div>
    );
    const encadre = (
        <div className="pub-eval-stagiaire">
            <div><strong>{v.personne} :</strong> {d.nom} {d.prenom}</div>
            {d.entreprise_nom && <div><strong>Entreprise :</strong> {d.entreprise_nom}</div>}
            {d.date_debut && <div><strong>Période :</strong> {dateFr(d.date_debut)} → {dateFr(d.date_fin)}</div>}
        </div>
    );
    const blocTotal = !masquer && (
        <div className="eval-total">
            <span className="eval-total-label"><i className="fa-solid fa-calculator" /> Total</span>
            <span className="eval-total-score">{total} <span className="eval-total-sur">/ {grille.total_max} pts</span></span>
        </div>
    );

    // ── Le RÉCAPITULATIF : ce que le maître signe, avant l'envoi définitif ───
    if (recap) {
        return (
            <div className="pub-page">
                <div className="form-public">
                    {enTete}
                    <div className="form-public-body">
                        {encadre}
                        <div className="form-section">
                            <h3><i className="fa-solid fa-list-check" /> Récapitulatif avant envoi</h3>
                            <p className="pub-aide pub-aide-bloc">Relisez votre évaluation : une fois envoyée, elle ne se modifie plus.</p>
                            {grille.criteres.map((c, i) => {
                                const n = choisi(c);
                                return (
                                    <div className="pub-eval-recap-ligne" key={c.id}>
                                        <div className="pub-eval-recap-crit">
                                            <span>{i + 1} — {c.libelle}</span>
                                            <strong>{n?.intitule}{!masquer && n ? ` · ${pts(n.points)}` : ''}</strong>
                                        </div>
                                        {c.commentaire_actif && commentaires[c.id]?.trim() && (
                                            <div className="pub-eval-recap-com">« {commentaires[c.id].trim()} »</div>
                                        )}
                                    </div>
                                );
                            })}
                            {blocTotal}
                            {commentaire.trim() && (
                                <div className="pub-eval-recap-ligne">
                                    <div className="pub-eval-recap-crit"><span>Commentaire général</span></div>
                                    <div className="pub-eval-recap-com" style={{ whiteSpace: 'pre-wrap' }}>« {commentaire.trim()} »</div>
                                </div>
                            )}
                            {v.signataire && (
                                <div className="pub-eval-recap-ligne">
                                    <div className="pub-eval-recap-crit"><span>Signataire</span><strong>{signataire.trim()}</strong></div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="form-public-footer pub-pied-eval">
                        <button type="button" className="pub-btn pub-btn-secondaire" onClick={() => setRecap(false)} disabled={enCours}>
                            <i className="fa-solid fa-pen" /> Modifier
                        </button>
                        <button type="button" className="pub-btn" onClick={envoyer} disabled={enCours}>
                            {enCours ? <><i className="fa-solid fa-circle-notch pub-tourne" /> Envoi…</>
                                     : <><i className="fa-solid fa-check" /> Envoyer l'évaluation</>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── La grille ────────────────────────────────────────────────────────────
    return (
        <div className="pub-page">
            <div className="form-public">
                {enTete}
                <div className="form-public-body">
                    {encadre}
                    {apercu && (
                        <div className="pub-encart pub-encart-info">
                            <i className="fa-solid fa-eye" /> Aperçu — données fictives. Cette page est celle que reçoit le destinataire ; rien n'est envoyé.
                        </div>
                    )}
                    <p className="pub-aide pub-aide-bloc">
                        {v.signataire
                            ? <>Merci d'évaluer le stage de <b>{d.prenom} {d.nom}</b> : choisissez un niveau pour chaque critère,
                                puis indiquez votre nom. Vous relirez votre évaluation avant de l'envoyer.</>
                            : <>Choisissez un niveau pour chaque critère. Vous relirez la fiche avant de l'enregistrer.</>}
                    </p>

                    {grille.criteres.map((c, i) => {
                        const n = choisi(c);
                        const nom = `crit-${c.id}`;
                        return (
                            <fieldset className="eval-critere" key={c.id}>
                                <legend className="eval-critere-title">
                                    <span>{i + 1} — {c.libelle}</span>
                                    {!masquer && <span className="eval-score">{n ? pts(n.points) : '— pts'}</span>}
                                </legend>
                                {c.sous_detail && <div className="pub-aide" style={{ marginBottom: 6 }}>{c.sous_detail}</div>}
                                <div className="eval-niveaux" role="radiogroup" aria-label={c.libelle}>
                                    {c.niveaux.map((niv) => (
                                        <label key={niv.id} className={'eval-niveau-btn' + (n?.id === niv.id ? ' selected' : '')}>
                                            <input type="radio" className="pub-sr" name={nom} value={niv.id}
                                                   checked={n?.id === niv.id}
                                                   onChange={() => setNiveaux((x) => ({ ...x, [c.id]: niv.id }))} />
                                            {niv.intitule}{!masquer ? ` — ${pts(niv.points)}` : ''}
                                        </label>
                                    ))}
                                </div>
                                {c.commentaire_actif && (
                                    <textarea className="pub-eval-com" placeholder="Commentaire…" maxLength={2000}
                                              aria-label={`Commentaire — ${c.libelle}`}
                                              value={commentaires[c.id] || ''}
                                              onChange={(e) => setCommentaires((x) => ({ ...x, [c.id]: e.target.value }))} />
                                )}
                            </fieldset>
                        );
                    })}

                    {blocTotal}

                    <div className="form-group form-group-large" style={{ marginBottom: 14 }}>
                        <label htmlFor="eval-commentaire">Commentaire général sur le stagiaire</label>
                        <textarea id="eval-commentaire" placeholder="Vos observations générales…" maxLength={4000}
                                  value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
                    </div>
                    {v.signataire && (
                        <div className="form-group form-group-large">
                            <label htmlFor="eval-signataire">Nom et fonction du signataire <span className="form-required">*</span></label>
                            <input id="eval-signataire" maxLength={200} autoComplete="name" placeholder="Ex : Marie GARCIA, responsable logistique"
                                   value={signataire} onChange={(e) => setSignataire(e.target.value)} />
                        </div>
                    )}
                    {erreur && <div className="pub-encart pub-encart-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                </div>
                <div className="form-public-footer pub-pied-depot">
                    {/* ⚠️ Grisé ET il dit ce qui manque, en toutes lettres (E69). */}
                    {manque.length > 0 && <div className="pub-aide pub-raison">Il manque {manque.join(' et ')}.</div>}
                    <button type="button" className="pub-btn" disabled={manque.length > 0} onClick={() => { setErreur(null); setRecap(true); window.scrollTo(0, 0); }}>
                        <i className="fa-solid fa-check" /> Valider l'évaluation
                    </button>
                </div>
            </div>
        </div>
    );
}

export default StageEvaluationPublique;
